import { TOUR_ANCHOR_ATTR } from '@bookora/ui';

import { onErrorShown } from '@/lib/feedback';

import { useTourStore } from '../tour-store';
import { reportStruggle, sectionKeyOf } from './store';

/**
 * The high-signal collectors of struggle v1 (critic C6). Each reads only what
 * `score.ts`'s privacy note allows: a section key, a tour anchor id, an error
 * code, and an opaque in-memory hash to tell «the same button» or «the same
 * form» apart. Rage clicks, ping-pong and idle detection are deliberately out:
 * they are noise on a calendar board and on long forms.
 *
 *  · repeated refusals — the same `ApiError` code on a page, via the toast;
 *  · validation bursts — `.ant-form-item-explain-error` appearing in a form
 *    right after a SUBMIT-LIKE press (a button pressed, or Enter), so errors
 *    that come and go while someone types a phone number are not counted
 *    (the kit Form stays unwrapped: `inputs.tsx` explains why);
 *  · blocked presses — a click OR a tap on a kit control that is blocked
 *    with a reason (a kit button's `disabledReason`, `.bk-button--blocked`, or
 *    a `DisabledReason` wrapper). The kit swallows the click, so the adapter
 *    here listens in the capture phase. The kit's own report —
 *    `BlockedReasonProvider onBlockedPress` — feeds the same function
 *    (`struggleBlockedPress`); one press reported by both is counted once, so
 *    the root provider can be mounted before this listener is retired.
 *
 * The form-abandon collector lives with the inline host (`inline-tour-host.ts`).
 */

/**
 * Refusals worth counting: the user's own 4xx — a validation the server
 * caught, a conflict, a rule of the clinic. Not a struggle with the page:
 * sign-in (401), billing read-only (402 — the banner explains), a missing
 * permission (403 — the toast already names it and who can grant it; a tour
 * cannot), a record gone (404 — a stale link), timeouts and rate limits.
 */
const IGNORED_STATUSES = new Set([401, 402, 403, 404, 408, 429]);

const BLOCKED_SELECTOR = '.bk-button--blocked, .bk-disabled-reason[aria-description]';
const ERROR_CLASS = 'ant-form-item-explain-error';
const FORM_SELECTOR = 'form, [data-help-context]';
/** Errors appearing this soon after a button press or Enter answer a submit. */
const SUBMIT_TO_ERROR_MS = 800;

/** A short, stable, in-memory hash (FNV-1a): «the same thing» without keeping the thing. */
export function opaqueKey(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

function currentSection(): string {
  return sectionKeyOf(useTourStore.getState().pathname);
}

function anchorOf(el: Element | null): string | null {
  return el?.closest(`[${TOUR_ANCHOR_ATTR}]`)?.getAttribute(TOUR_ANCHOR_ATTR) ?? null;
}

/** The identity of a blocked button: its anchor, else a hash of its reason and label. */
function buttonKey(button: Element): string {
  const anchor = button.getAttribute(TOUR_ANCHOR_ATTR);
  if (anchor) return `a:${anchor}`;
  const reason = button.getAttribute('aria-description') ?? '';
  const label = button.getAttribute('aria-label') ?? button.textContent ?? '';
  return `h:${opaqueKey(`${reason}\u0000${label}`)}`;
}

/** The identity of a form: its declared help context, else its anchor, else a hash of its position. */
function formKey(form: Element | null): string {
  if (!form) return 'page';
  const context = form.getAttribute('data-help-context');
  if (context) return `c:${context}`;
  const anchor = anchorOf(form);
  if (anchor) return `a:${anchor}`;
  const id = form.getAttribute('id') ?? form.getAttribute('name');
  if (id) return `h:${opaqueKey(id)}`;
  const dialog = form.closest('[role="dialog"]');
  return dialog ? 'dialog' : 'page';
}

function hasErrorNode(node: Node): Element | null {
  if (!(node instanceof Element)) return null;
  if (node.classList.contains(ERROR_CLASS)) return node;
  const inner = node.getElementsByClassName(ERROR_CLASS);
  return inner.length > 0 ? (inner[0] ?? null) : null;
}

function countBlockedPress(element: Element): void {
  reportStruggle({
    type: 'blocked-press',
    section: currentSection(),
    target: buttonKey(element),
    anchor: anchorOf(element),
  });
}

/**
 * The press the capture listener just counted, until the kit's own report of
 * the SAME press arrives (later in the same dispatch) or the task ends.
 */
let countedPress: Element | null = null;

/**
 * One press on a blocked control, as the kit reports it
 * (`BlockedReasonProvider onBlockedPress` — its `BlockedPressInfo` fits this
 * parameter). Taps count like clicks: on a tablet a press is the only signal
 * there is. A press the capture listener already counted is not counted
 * twice, so the root provider and the listener can coexist. Only the
 * control's identity is kept (its anchor, else an opaque hash of its reason
 * and label).
 */
export function struggleBlockedPress(info: { element: Element }): void {
  const element = info.element.closest(BLOCKED_SELECTOR) ?? info.element;
  if (countedPress === element) {
    countedPress = null;
    return;
  }
  countBlockedPress(element);
}

/** Installs every collector; returns the uninstall. Safe to call once per app. */
export function installStruggleCollectors(): () => void {
  if (typeof document === 'undefined') return () => undefined;

  let lastSubmitAt = 0;
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Enter') lastSubmitAt = Date.now();
  };
  document.addEventListener('keydown', onKey, true);

  const onClick = (event: MouseEvent): void => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('button, [role="button"]')) lastSubmitAt = Date.now();
    const blocked = target?.closest(BLOCKED_SELECTOR);
    if (!blocked) return;
    countBlockedPress(blocked);
    countedPress = blocked;
    setTimeout(() => {
      if (countedPress === blocked) countedPress = null;
    }, 0);
  };
  document.addEventListener('click', onClick, true);

  const observer =
    typeof MutationObserver === 'undefined'
      ? null
      : new MutationObserver((mutations) => {
          if (document.visibilityState === 'hidden') return;
          if (Date.now() - lastSubmitAt > SUBMIT_TO_ERROR_MS) return;
          const seen = new Set<string>();
          for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
              const error = hasErrorNode(node);
              if (!error) continue;
              const form = error.closest(FORM_SELECTOR);
              const key = formKey(form);
              if (seen.has(key)) continue;
              seen.add(key);
              reportStruggle({
                type: 'validation',
                section: currentSection(),
                form: key,
                anchor: anchorOf(error),
              });
            }
          }
        });
  observer?.observe(document.body, { childList: true, subtree: true });

  const offErrors = onErrorShown((error) => {
    if (error.statusCode < 400 || error.statusCode >= 500) return;
    if (IGNORED_STATUSES.has(error.statusCode)) return;
    const focused = document.activeElement instanceof Element ? document.activeElement : null;
    reportStruggle({
      type: 'api-error',
      section: currentSection(),
      code: error.code,
      anchor: anchorOf(focused),
    });
  });

  return () => {
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('keydown', onKey, true);
    observer?.disconnect();
    offErrors();
  };
}
